import {defineStore} from "pinia";
import {OrganizationService} from "../services/organization.service";
import {ApplicationHelper} from "../helpers/ApplicationHelper";

export const useOrganizationStore = defineStore('organization', {

  state: () => ({
    organizationService: new OrganizationService(),
    organization: {},
    organizationId: null,
    editSettings: false,
    localOrganization: null,
    errors: {},
    pagePermissions: {},
    permissions: {},
    loading: true,
    exportingDonorList: false,
    exportingDonationList: false, // these are for analytic events
    traceableType: "Organization",
    traceableName: "organization",

  }),

  actions: {
    setOrganizationId(id) {
      if (id) {
        this.organizationId = id;
      } else {
        throw `Organization ID is not set: ${id}`;
      }
    },

    getOrganization() {
      this.loading = true;
      if (this.organizationId) {
        return this.organizationService.show(this.organizationId).then((res) => {
          this.organization = res.record;
          this.permissions = res.permissions;

          this.localOrganization = this.organization;
          this.loading = false;
          return true;
        });
      }
      return true;
    },

    async getPagePermissions() {
      if (this.organizationId) {
        const res = await this.organizationService.getPermissions(this.organizationId);
        this.pagePermissions = res.json;
      }
    },

    toggleEditSettings() {
      this.editSettings = !this.editSettings;
      if (this.editSettings) {
        this.localOrganization = {...this.organization};  // Create a copy of organization data
      } else {
        this.localOrganization = null;  // Discard the copy if editing is canceled
        this.errors = {};
      }
    },

    async saveSettings() {
      if (this.validateForm()) {
        this.loading = true;
        if (this.localOrganization) {
          try {
             await this.organizationService.update(this.organization.id, this.localOrganization);
            this.organization = {...this.localOrganization};  // Update original organization data
            this.toggleEditSettings();
            this.errors = {};
            this.loading = false;
          } catch (error) {
            console.error("Failed to save organization settings", error);
          }
        }
      }
    },

    cancelEditSettings() {
      this.toggleEditSettings();  // Just toggle off edit mode and discard changes
    },

    validateForm() {
      let valid = true;
      this.errors = {}; // Reset errors
      const urlError = " URL is invalid. It should start with 'http://' or 'https://' and be a properly formatted URL.";

      // Validate name
      if (!this.localOrganization.name || this.localOrganization.name.length < 1) {
        this.errors['name'] = "Organization name can't be empty and must be at least 1 character";
        valid = false;
      }

      // Validate phone number (supports optional country code, like +1 (222)-222-2222, +1 222-222-2222, 222 222 2222)
      const phoneNumberPattern = /^\+?\s?(\(?\d{1,3}\)?[-\s]?)?\(?\d{3}\)?[-\s]?\d{3}[-\s]?\d{4}$/;
      if (this.localOrganization.phone_number && !phoneNumberPattern.test(this.localOrganization.phone_number)) {
        this.errors['phone_number'] = "The phone number provided is invalid. Acceptable formats include +1 (222)-222-2222, (222)-222-2222, or 222 222 2222.";
        valid = false;
      }

      // Validate email
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (this.localOrganization.email && !emailPattern.test(this.localOrganization.email)) {
        this.errors['email'] = "The email address is invalid. Please ensure it follows the format example@example.com.";
        valid = false;
      }

      // Validate website URL
      const urlPattern = /^(https?:\/\/)[^\s$.?#].[^\s]*$/i;
      if (this.localOrganization.website_url && !urlPattern.test(this.localOrganization.website_url)) {
        this.errors['website_url'] = "The website" + urlError;
        valid = false;
      }

      // Validate social media URLs
      if (this.localOrganization.facebook_url && !urlPattern.test(this.localOrganization.facebook_url)) {
        this.errors['facebook_url'] = "The Facebook" + urlError;
        valid = false;
      }
      if (this.localOrganization.instagram_url && !urlPattern.test(this.localOrganization.instagram_url)) {
        this.errors['instagram_url'] = "The Instagram" + urlError;
        valid = false;
      }
      if (this.localOrganization.youtube_url && !urlPattern.test(this.localOrganization.youtube_url)) {
        this.errors['youtube_url'] = "The YouTube" + urlError;
        valid = false;
      }
      if (this.localOrganization.linkedin_url && !urlPattern.test(this.localOrganization.linkedin_url)) {
        this.errors['linkedin_url'] = "The LinkedIn" + urlError;
        valid = false;
      }
      if (this.localOrganization.x_url && !urlPattern.test(this.localOrganization.x_url)) {
        this.errors['x_url'] = "The X" + urlError;
        valid = false;
      }

      return valid;
    }
  }, getters: {
    showPanel: state => (panel) => {
      return true; //disabling dynamic permissions for now
      // return state.pagePermissions[panel] && state.pagePermissions[panel] === true;
    },
  }
});
